import { useEffect, useRef, useState } from "react";
import type { TransportState } from "@wr/ui";
import type { ScreenTransport } from "./TransportProvider";

/**
 * The chrome's transport state (design spec §13.1), merged from three signals.
 *
 *  - `navigator.onLine === false` → `browser-offline`. The banner says "no
 *    internet" and offers **no action**, because there is nothing to retry.
 *  - **the active screen's own report** (`TransportProvider`) → `reconnecting`
 *    or `offline`, with the screen's own `Retry`. This is the signal the shell
 *    used to be missing entirely: a screen with a socket knows things about the
 *    transport that neither of the other two can see, and T-191 B2 is what
 *    happens without it — the banner declaring the network fine while the
 *    lobby's socket was dead and its content frozen.
 *  - the last session refresh failed at the transport layer → `offline`, with a
 *    `Retry`, because §13.2 part 2 says a screen with no in-content remedy
 *    passes one.
 *
 * Precedence is deliberate: the **device** first (no network beats every other
 * diagnosis and is the only one the player can act on), then the **screen**
 * (the most specific report available), then the session.
 *
 * What it deliberately does NOT do is poll. A heartbeat that exists only to
 * colour a bar is a request per player per interval forever.
 *
 * `restored` is emitted for two seconds after the merged state returns to
 * `online` **from any of the three**, so the green "Back online." bar has
 * something to key on; the banner component owns its own 1600ms auto-dismiss.
 * Keying it on the merged state rather than on `navigator.onLine` is the fix
 * for the arc Quinn measured: the browser came back, the bar went green and
 * vanished, and the socket — the thing that was actually broken — was still
 * down.
 */
/**
 * `navigator.onLine`, as React state.
 *
 * Extracted (T-260) because the **device** half of the merge is also, on its
 * own, the device half of the coin plate's stale dim (Ivy's profile §9.2): the
 * balance is the one live number on the profile screen, and no screen there owns
 * a socket, so `browser-offline` is the only transport fact that reaches it.
 * `useBalanceDim` reads this rather than re-deriving "is the device offline"
 * from a `TransportState` string — two derivations of one fact is how the chip
 * and the plate end up disagreeing about the same wallet.
 */
export function useBrowserOnline(): boolean {
  const [browserOnline, setBrowserOnline] = useState(() =>
    typeof navigator === "undefined" ? true : navigator.onLine,
  );

  useEffect(() => {
    function goOnline() {
      setBrowserOnline(true);
    }
    function goOffline() {
      setBrowserOnline(false);
    }
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  return browserOnline;
}

export function useNetworkStatus(
  apiOffline: boolean,
  screen: ScreenTransport = {
    state: "online",
    retry: null,
    reporting: false,
    stale: false,
    immersive: false,
  },
): TransportState {
  const browserOnline = useBrowserOnline();
  const [recovered, setRecovered] = useState(false);

  const raw: TransportState = !browserOnline
    ? "browser-offline"
    : screen.state === "reconnecting" || screen.state === "offline"
      ? screen.state
      : apiOffline
        ? "offline"
        : "online";

  /**
   * The green bar is earned by a **recovery**, and a screen unmounting is not
   * one. Navigating away from a frozen lobby clears its report, which lands
   * `raw` on `online` — announcing "Back online." there would be the banner
   * outliving its screen with a claim nothing supports.
   */
  const previous = useRef({ raw, reporting: screen.reporting });
  useEffect(() => {
    const wasReportedByAScreen = previous.current.raw !== "online" && previous.current.reporting;
    const reporterLeft = wasReportedByAScreen && !screen.reporting;
    if (previous.current.raw !== "online" && raw === "online" && !reporterLeft) setRecovered(true);
    previous.current = { raw, reporting: screen.reporting };
  }, [raw, screen.reporting]);

  useEffect(() => {
    if (!recovered) return;
    const timer = setTimeout(() => setRecovered(false), 2000);
    return () => clearTimeout(timer);
  }, [recovered]);

  return recovered && raw === "online" ? "restored" : raw;
}
